<script lang="ts">
  /*
   * Small floating action group anchored to the top-right of whichever pane
   * the user is looking at. Holds the high-frequency document actions
   * (Copy markdown, Clear document) that used to live in the header but
   * benefit from being on the same surface as the content.
   *
   * Hidden when the document is empty (the EmptyState fills the pane and
   * the actions have nothing to act on).
   */
  import { Copy, Trash2 } from '@lucide/svelte'

  interface Props {
    onCopy: () => void
    onClear: () => void
  }
  let { onCopy, onClear }: Props = $props()
</script>

<div class="pane-toolbar" role="group" aria-label="Document actions">
  <button
    type="button"
    class="btn"
    title="Copy markdown"
    aria-label="Copy markdown"
    onclick={onCopy}
  >
    <Copy size={16} strokeWidth={1.75} aria-hidden="true" />
  </button>
  <button
    type="button"
    class="btn danger"
    title="Clear document"
    aria-label="Clear document"
    onclick={onClear}
  >
    <Trash2 size={16} strokeWidth={1.75} aria-hidden="true" />
  </button>
</div>

<style>
  .pane-toolbar {
    position: absolute;
    top: var(--space-3);
    right: var(--space-3);
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 2px;
    background: color-mix(in srgb, var(--bg-surface) 88%, transparent);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-sm);
    backdrop-filter: blur(6px);
    z-index: 5;
    opacity: 0.55;
    transition: opacity var(--motion-fast) var(--ease-out);
  }
  .pane-toolbar:hover,
  .pane-toolbar:focus-within {
    opacity: 1;
  }
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    border-radius: var(--radius-sm);
    background: transparent;
    color: var(--text-secondary);
    cursor: pointer;
  }
  .btn:hover {
    color: var(--text-primary);
    background: var(--bg-sunken);
  }
  .btn.danger:hover {
    color: var(--callout-caution);
    background: color-mix(in srgb, var(--callout-caution) 14%, transparent);
  }
  .btn:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }
  @media (max-width: 768px) {
    .pane-toolbar {
      top: var(--space-2);
      right: var(--space-2);
    }
  }
</style>
